/**
 * `<Toaster />` — Sonner host. Mounted once at the root client renderer.
 *
 * Returns `null` during SSR; on the client the lazy-loaded Sonner module
 * mounts the actual toast region. This avoids shipping the Sonner runtime
 * to the SSR bundle and keeps the `node` Vitest environment happy.
 *
 * Visual styling matches the surveillance-style design tokens in
 * `apps/web/src/styles/index.css` (dark surfaces + thin accent borders).
 */

import { useEffect, useState } from "react";

type SonnerModule = typeof import("sonner");

type ToasterProps = {
  dir?: "ltr" | "rtl";
};

export function Toaster({ dir = "ltr" }: ToasterProps) {
  const [mod, setMod] = useState<SonnerModule | null>(null);

  useEffect(() => {
    let mounted = true;
    void import("sonner").then((m) => {
      if (mounted) setMod(m);
    });
    return () => {
      mounted = false;
    };
  }, []);

  if (!mod) return null;
  const SonnerToaster = mod.Toaster;
  return (
    <SonnerToaster
      dir={dir}
      position="top-center"
      richColors
      closeButton
      theme="dark"
      toastOptions={{
        style: {
          background: "var(--color-bg-surface-elevated)",
          border: "1px solid var(--color-border-default)",
          color: "var(--color-fg-default)",
        },
      }}
    />
  );
}
